import { FC, KeyboardEvent as ReactKeyboardEvent, useEffect, useRef, useState } from 'react';
import { IonButton } from '@ionic/react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { createUseStyles } from 'react-jss';
import WaveSurfer from 'wavesurfer.js';
import { IRecording } from '../../recording/RecordingState';
import { formatTime } from '../../recording/formatTime';
import { PlayIcon, PauseIcon } from '../../theme/Icons';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';
import { CareMapState } from '../../caremap/CareMapState';
import { Logger } from '../../support/Logger';

const SKELETON_BAR_COUNT = 70;
const KEYBOARD_SEEK_SECONDS = 5;

const useStyles = createUseStyles({
    '@keyframes waveformSkeletonPulse': {
        '0%, 100%': { opacity: 0.25 },
        '50%': { opacity: 0.7 }
    },
    skeletonBar: {
        flex: 1,
        backgroundColor: 'var(--ion-color-step-300, #b3bfc3)',
        borderRadius: 3,
        animation: '$waveformSkeletonPulse 1.2s ease-in-out infinite'
    },
    waveformContainer: {
        height: '100%',
        outline: 'none',
        '&:focus-visible': {
            boxShadow: '0 0 0 2px var(--ion-color-primary)',
            borderRadius: 4
        }
    }
});

export const RecordingPlayer: FC<{ recording: IRecording; noteId: string }> = ({ recording, noteId }) => {
    const classes = useStyles();
    const tracking = useInstance(MixPanelEventEmitter);
    const careMapState = useInstance(CareMapState);
    const logger = useInstance(Logger);
    const careMapId = careMapState.getActiveCaremap()?.careMapId;

    const containerRef = useRef<HTMLDivElement>(null);
    const wavesurferRef = useRef<WaveSurfer | null>(null);
    // Fires the tracking event once per RecordingPlayer mount. Because the player
    // is conditionally rendered behind the Recording tab, tab-toggling Recording→
    // Summary→Recording produces a fresh mount and the event fires again.
    const hasTrackedPlayRef = useRef(false);

    const [isReady, setIsReady] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);

    useEffect(() => {
        if (!containerRef.current) {
            return;
        }

        // wavesurfer renders to a canvas; canvas color strings can't be CSS variables,
        // so resolve the Ionic theme values from the document at mount time. Theme
        // toggles after mount won't repaint — Innerhive doesn't have a runtime theme
        // toggle today, so this is acceptable.
        const root = getComputedStyle(document.documentElement);
        const waveColor = root.getPropertyValue('--ion-color-step-300').trim() || '#b3bfc3';
        const progressColor = root.getPropertyValue('--ion-color-primary').trim() || '#11789D';

        const ws = WaveSurfer.create({
            container: containerRef.current,
            url: recording.url,
            height: 28,
            waveColor,
            progressColor,
            cursorWidth: 0,
            barWidth: 2,
            barGap: 1,
            barRadius: 2,
            normalize: true,
            mediaControls: false,
            interact: true,
            dragToSeek: true
        });

        ws.on('ready', () => {
            setDuration(ws.getDuration());
            setIsReady(true);
        });
        ws.on('play', () => setIsPlaying(true));
        ws.on('pause', () => setIsPlaying(false));
        ws.on('finish', () => setIsPlaying(false));
        ws.on('timeupdate', t => setCurrentTime(t));
        ws.on('error', err => {
            logger.error(err, { message: 'Recording player failed to load', recordingUrl: recording.url });
            setLoadFailed(true);
        });

        wavesurferRef.current = ws;

        return () => {
            ws.destroy();
            wavesurferRef.current = null;
        };
    }, [recording.url, logger]);

    const handleToggle = () => {
        const ws = wavesurferRef.current;
        if (!ws) {
            return;
        }
        const wasPlaying = ws.isPlaying();
        ws.playPause();
        if (!wasPlaying && !hasTrackedPlayRef.current) {
            hasTrackedPlayRef.current = true;
            tracking.track('Recording Played From Note', () => ({
                noteId,
                careMapId,
                durationMs: recording.msDuration
            }));
        }
    };

    const handleKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
        const ws = wavesurferRef.current;
        if (!ws || !isReady) {
            return;
        }
        if (e.key === 'ArrowRight') {
            e.preventDefault();
            ws.setTime(Math.min(ws.getDuration(), ws.getCurrentTime() + KEYBOARD_SEEK_SECONDS));
        } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            ws.setTime(Math.max(0, ws.getCurrentTime() - KEYBOARD_SEEK_SECONDS));
        } else if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            handleToggle();
        }
    };

    const totalSeconds = duration || recording.msDuration / 1000;
    const displayTime = isPlaying
        ? formatTime(currentTime)
        : formatTime(totalSeconds);

    return (
        <div
            data-id="recording-player"
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 16px',
                border: '1px solid var(--ion-color-light)',
                borderRadius: 12,
                margin: '12px 0'
            }}
        >
            <IonButton
                fill="clear"
                size="small"
                onClick={handleToggle}
                data-id={isPlaying ? 'recording-player-pause' : 'recording-player-play'}
            >
                {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </IonButton>

            <div style={{ flex: 1, minWidth: 0, height: 28, position: 'relative' }}>
                <div
                    ref={containerRef}
                    data-id="recording-player-waveform"
                    role="slider"
                    tabIndex={0}
                    aria-label="Recording playback position"
                    aria-valuemin={0}
                    aria-valuemax={Math.round(totalSeconds)}
                    aria-valuenow={Math.round(currentTime)}
                    aria-valuetext={`${formatTime(currentTime)} of ${formatTime(totalSeconds)}`}
                    onKeyDown={handleKeyDown}
                    className={classes.waveformContainer}
                    style={{ opacity: isReady ? 1 : 0 }}
                />
                {!isReady && !loadFailed && (
                    <div
                        aria-hidden="true"
                        data-id="recording-player-waveform-skeleton"
                        style={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            pointerEvents: 'none'
                        }}
                    >
                        {Array.from({ length: SKELETON_BAR_COUNT }).map((_, i) => {
                            const heightPct = 30 + ((i * 37) % 50);
                            const delayMs = (i % 8) * 80;
                            return (
                                <div
                                    key={i}
                                    className={classes.skeletonBar}
                                    style={{
                                        height: `${heightPct}%`,
                                        animationDelay: `${delayMs}ms`
                                    }}
                                />
                            );
                        })}
                    </div>
                )}
                {loadFailed && (
                    <div
                        data-id="recording-player-load-failed"
                        style={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 13,
                            color: 'var(--ion-color-step-600, #666666)'
                        }}
                    >
                        Couldn’t load audio
                    </div>
                )}
            </div>

            <span
                style={{
                    fontSize: 12,
                    color: 'var(--ion-color-step-600, #666666)',
                    minWidth: 44,
                    textAlign: 'right'
                }}
            >
                {displayTime}
            </span>
        </div>
    );
};
